import type { Content } from '@/modules/Entertainment/interface/Content';
import type { FC } from 'react';
import Link from 'next/link';
import { Box, Flex, Text, type FlexProps } from '@eqi-investimentos/react';
import { Button, Image, Progress } from '@chakra-ui/react';
import { PATHS } from '@/config/paths';
import { CardFlipHorizontal } from '@/components/atoms/CardFlipHorizontal';
import { Icon } from '@/components/atoms/Icon';
import { IfRender } from '@/utils/jsx';
export const ContentProgressCard: FC<ContentProgressCardProps> = ({
  description,
  subtitle,
  thumbAlt,
  thumbUrl,
  title,
  containerProps,
  hideProgress = false,
  isEditable = false,
  isEditMode = false,
  isSelectedToDelete = false,
  progress = 0,
  slug = '#',
  handleDeleteItem
}) => <Flex as={Link} href={mountHref(slug, isEditable, isEditMode)} onClick={e => isEditable && isEditMode ? e.stopPropagation() : null} flexDir="column" justifyContent="space-between" w="full" maxW="21.875rem" h="11.25rem" minH="9.375rem" p="l" border="1px solid" borderColor="border.secondary" borderRadius="lg" transition="background 0.2s ease-in-out" {...containerProps} _hover={{
  bg: 'background.brandLight'
}} data-sentry-element="Flex" data-sentry-component="ContentProgressCard" data-sentry-source-file="index.tsx">
    <Flex justifyContent="space-between" alignItems="center" gap="3xs" w="full" h="full" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
      <Flex flexDir="column" w="calc(100% - 6.125rem)" gap="xs" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
        <Text size="l" fontWeight="light" color="content.brand" noOfLines={1} data-sentry-element="Text" data-sentry-source-file="index.tsx">
          {title}
        </Text>
        <Text size="l" color="content.secondary" noOfLines={2} data-sentry-element="Text" data-sentry-source-file="index.tsx">
          {subtitle}
        </Text>
        <IfRender condition={!!description} data-sentry-element="IfRender" data-sentry-source-file="index.tsx">
          <Text size="m" fontWeight="light" color="content.tertiary" data-sentry-element="Text" data-sentry-source-file="index.tsx">
            {description}
          </Text>
        </IfRender>
      </Flex>
      <IfRender condition={!isEditable} data-sentry-element="IfRender" data-sentry-source-file="index.tsx">
        <Image src={thumbUrl} alt={thumbAlt} w="5.5rem" h="3.25rem" borderRadius="2xs" data-sentry-element="Image" data-sentry-source-file="index.tsx" />
      </IfRender>
      <IfRender condition={isEditable} data-sentry-element="IfRender" data-sentry-source-file="index.tsx">
        <CardFlipHorizontal showBack={isEditable && isEditMode} minH="0.625rem" frontFace={<Box w="5.625rem" h="3.125rem">
              <Image src={thumbUrl} alt={thumbAlt} w="5.5rem" h="3.25rem" borderRadius="md" objectFit="cover" />
            </Box>} backFace={<Box pos="relative" w="5.625rem" h="3.125rem">
              <Button variant="naked" onClick={() => {
          if (handleDeleteItem) {
            handleDeleteItem();
          }
        }} pos="absolute" display="flex" flexDir="column" alignItems="center" justifyContent="center" w="5.625rem" h="3.125rem" bgColor={isSelectedToDelete ? 'action.active.brand' : 'background.inverse'} opacity={isSelectedToDelete ? 1 : 0.7} borderRadius="base" zIndex="docked" _hover={{
          bg: isSelectedToDelete ? 'action.active.brand' : 'background.inverse'
        }}>
                <Icon iconName="Trash" width={24} height={24} color="background.alwaysLight" cursor="pointer" />
              </Button>
              <Image src={thumbUrl} alt={thumbAlt} w="5.625rem" h="3.125rem" borderRadius="base" objectFit="cover" />
            </Box>} data-sentry-element="CardFlipHorizontal" data-sentry-source-file="index.tsx" />
      </IfRender>
    </Flex>
    <IfRender condition={!hideProgress} data-sentry-element="IfRender" data-sentry-source-file="index.tsx">
      <Progress w="full" value={progress} borderRadius="lg" sx={{
      h: '0.5rem',
      mt: '0.625rem',
      bg: 'transparency.light',
      '[role=progressbar]': {
        bg: 'action.active.brand'
      }
    }} data-sentry-element="Progress" data-sentry-source-file="index.tsx" />
    </IfRender>
  </Flex>;
const mountHref = (slug: string, isEditable: boolean, isEditMode: boolean) => {
  if (!isEditable) {
    return `${PATHS.hubs.content}/${slug}`;
  }
  if (isEditMode) {
    return '#';
  }
  return slug;
};
export type ContentProgressCardProps = {
  description: string;
  subtitle: Content['summary'];
  thumbAlt: string;
  thumbUrl: Content['thumb'];
  title: Content['title'];
  containerProps?: FlexProps;
  hideProgress?: boolean;
  isEditable?: boolean;
  isEditMode?: boolean;
  isSelectedToDelete?: boolean;
  progress?: number;
  slug?: Content['slug'];
  handleDeleteItem?: () => void;
};